import React, { useCallback, useEffect, useRef, useState } from "react";
import { secondary } from "../../theme";
import { useApi } from "../../hooks/api";
import { useGlobal } from "../../hooks/global";
import { useNavigate, useSearchParams } from "react-router-dom";
import { expirationOptions, readContractExpiration } from "../../utils/contractExpiration";
import { FiHome, FiSend } from "react-icons/fi";
import { FaFileContract } from "react-icons/fa";
import { ITable } from "../../components/TableList";
import { IoAddCircleOutline } from "react-icons/io5";
import { PageList } from "../../components/PageList";
import { getFilterFields } from "../../utils/filterFields";
import { contract_status } from "../../utils/contract_status";
import { ALL_USERS, ROOT_USERS, STAFF_USERS } from "../../utils/users.groups";
import { ModalContract } from "../../components/Modals/ModalContract";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { ModalAttention } from "../../components/Modals/ModalAttention";
import { getFranchise, getAuthor, getStatus } from "../../utils/columnsTables";
import {
  copyOf,
  correctDate,
} from "../../services/generalServices";
import {
  contractsAttributes,
  historyAttributes,
  IActions,
  IFilters,
  ITagList,
} from "../../interfaces";

const initialFilters: IFilters[] = [
  getFilterFields("franchise_id", ROOT_USERS),
  getFilterFields("bond_type", ALL_USERS),
  getFilterFields("contract_author_id", STAFF_USERS),
  getFilterFields("document_eletronic_status", ALL_USERS),
  getFilterFields("range_picker", ALL_USERS),
  { key: "expiration", name: "Expiração", type: "select", noIcon: true, options: expirationOptions, userTypes: ALL_USERS },
];

const initialTable: ITable = {
  data: [],
  columns: [
    {
      attr: "friendly_id",
      name: "Código",
      style: {},
      styleContent: { fontSize: 16, fontWeight: 600 },
    },
    {
      attr: "name",
      name: "Nome",
      limitWidth: true,
      style: {},
      styleContent: { fontSize: 16, fontWeight: 600 },
    },
    {
      attr: "bond",
      name: "Vínculo",
      style: { textAlign: "center" },
      styleContent: {},
    },
    { attr: "signatories", name: "Signatário(s)", style: {}, styleContent: {} },
    {
      attr: "author",
      name: "Autor",
      style: { textAlign: "center", whiteSpace: "nowrap" },
      styleContent: { textAlign: "center" },
    },
    {
      attr: "status",
      name: "Status",
      style: { textAlign: "center" },
      styleContent: { textAlign: "center" },
    },
    {
      attr: "createdAt",
      name: "Criação",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
  ],
};

const initialTableRoot: ITable = {
  data: [],
  columns: [
    {
      attr: "friendly_id",
      name: "Código",
      style: {},
      styleContent: { fontSize: 16, fontWeight: 600 },
    },
    {
      attr: "name",
      name: "Nome",
      limitWidth: true,
      style: {},
      styleContent: { fontSize: 16, fontWeight: 600 },
    },
    {
      attr: "bond",
      name: "Vínculo",
      style: { textAlign: "center" },
      styleContent: {},
    },
    {
      attr: "franchise",
      name: "Imobiliária",
      style: { textAlign: "center" },
      styleContent: { textAlign: "center" },
    },
    {
      attr: "status",
      name: "Status",
      style: { textAlign: "center" },
      styleContent: { textAlign: "center" },
    },
    {
      attr: "createdAt",
      name: "Criação",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
    {
      attr: "signedAt",
      name: "Assinatura",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
    {
      attr: "canceledAt",
      name: "Cancelamento",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
  ],
};

const actionsInitial: IActions[] = [
  {
    main_option: {
      text: "Modelos de Documentos Eletrônicos",
      id: "go_to_models",
    },
    className: "go_to_models",
    icon: <FaFileContract size={20} />,
    options: [],
    type: "outline",
    canAccess: ["responsible", "manager"],
  },
  {
    main_option: {
      text: "Novo Documento Eletrônico",
      id: "new_item",
    },
    className: "new_item",
    icon: <IoAddCircleOutline size={20} className="new_item" />,
    options: [],
    type: "solid",
    canAccess: ["responsible", "manager", "broker", "analyst"],
  },
];

const getBond: React.FC<ITagList> = ({ theme, contract }) => {
  if (!contract.property_id && !contract.proposal_id)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          fontStyle: "italic",
          opacity: 0.7,
          fontSize: 14,
        }}
      >
        NENHUM
      </div>
    );
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 5,
      }}
    >
      <div style={{ display: "flex" }}>
        <div
          style={{
            padding: 5,
            whiteSpace: "nowrap",
            alignItems: "center",
            gap: 5,
            display: "flex",
            paddingLeft: 7,
            paddingRight: 7,
            borderRadius: 3,
            background: secondary[theme],
            color: "#FFF",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          <div>
            {contract.property_id ? <FiHome size={13} /> : <FiSend size={13} />}
          </div>
          {contract.property_id
            ? `Imóvel - #${contract.property?.friendly_id}`
            : `Proposta - #${contract.proposal?.friendly_id}`}
        </div>
      </div>
    </div>
  );
};

const getSignatories: React.FC<ITagList> = ({ theme, contract }) => {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
      {contract.signatories.map((signatory: any) => {
        return (
          <div style={{ display: "flex" }}>
            <div
              style={{
                padding: 5,
                whiteSpace: "nowrap",
                display: "flex",
                paddingLeft: 7,
                paddingRight: 7,
                borderRadius: 3,
                background: secondary[theme],
                color: "#FFF",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {signatory.name}
            </div>
          </div>
        );
      })}
      {contract.signatories.length === 0 && (
        <span style={{ fontStyle: "italic", opacity: 0.7, fontSize: 14 }}>
          Nenhum(a)
        </span>
      )}
    </div>
  );
};

export const ContractsList: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const navigation = useRef({ query: params.toString(), key: 0 });
  if (navigation.current.query !== params.toString()) {
    navigation.current = { query: params.toString(), key: navigation.current.key + 1 };
  }
  // Preserve the applied form on submission; reset it for external navigation.
  const onChangeExpiration = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("expiration", value);
    else next.delete("expiration");
    navigation.current.query = next.toString();
    setParams(next);
  };
  return <ContractsListContent key={navigation.current.key} onChangeExpiration={onChangeExpiration} />;
};

const ContractsListContent: React.FC<{ onChangeExpiration: (value: string) => void }> = ({ onChangeExpiration }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const expiration = readContractExpiration(params.get("expiration"));
  const rootFranchiseId = params.get("franchise_id");

  const { api, user } = useApi();
  const { theme, notify, paginationLimit, franchise } = useGlobal();

  const [atualPage, setAtualPage] = useState(1);
  const [filters, setFilters] = useState<any>(() =>
    ({ expiration, ...(user.type === "root" && rootFranchiseId ? { franchise_id: rootFranchiseId } : {}) }));
  const requestId = useRef(0);
  const listFilters = initialFilters
    .map(filter => ({ ...filter, ...(filter.key === "expiration"
      ? { value: expirationOptions.find(option => option.value === expiration) } : {}), ...(filter.key === "franchise_id" && rootFranchiseId && user.type === "root"
      ? { value: { value: rootFranchiseId, label: "Imobiliária selecionada no dashboard" } } : {}) }));
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [numOfContracts, setNumOfContracts] = useState(0);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [searchString, setSearchString] = useState<string>("");
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [openModalContract, setOpenModalContract] = useState(false);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false);
  const [contractSelected, setContractSelected] =
    useState<contractsAttributes | null>(null);
  const [table, setTable] = useState<ITable>(
    () => ({
      ...(user.type === "root" ? initialTableRoot : initialTable),
      columns: [...(user.type === "root" ? initialTableRoot : initialTable).columns,
        { attr: "expiration", name: "Expiração", style: { textAlign: "center" }, styleContent: { fontSize: 14, textAlign: "center" } },
        ...(expiration === "recently_expired" ? [{ attr: "updatedAt", name: "Atualização", style: { textAlign: "center" as const }, styleContent: { fontSize: 14, textAlign: "center" as const } }] : []),
      ],
    })
  );

  const searchContracts = useCallback(
    async (page, _filters = null) => {
      const currentRequest = ++requestId.current;
      setAtualPage(page);
      setLoadingContracts(true);
      try {
        const filters_obj = _filters
          ? { ..._filters }
          : { ...filters, search: searchString };
        const selectedExpiration = readContractExpiration(filters_obj.expiration || null);
        if (selectedExpiration !== expiration) onChangeExpiration(selectedExpiration);
        if (selectedExpiration) {
          filters_obj.expiration = selectedExpiration;
          delete filters_obj.status;
        }

        let period: any = {};
        period.date_init = filters_obj.date_init ? filters_obj.date_init : null;
        period.date_end = filters_obj.date_end ? filters_obj.date_end : null;
        if (!period.date_init || !period.date_end) period = undefined;
        else period = JSON.stringify([period.date_init, period.date_end]);

        filters_obj.period = period;

        const query = new URLSearchParams({ page: String(page), limit: String(paginationLimit) });
        Object.entries(filters_obj).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
        });
        // An empty root scope explicitly means all franchises, even if a global
        // franchise was previously selected in another part of the application.
        if (user.type === "root" && !query.has("franchise_id")) query.set("franchise_id", "");
        const url = `/generic_searches/contracts?${query}`;

        const result = await api.get(url);
        if (currentRequest !== requestId.current) return;

        let new_contracts: contractsAttributes[] = result.data.rows;

        setTable((atual) => {
          const new_table = { ...atual };
          new_table.columns = new_table.columns.filter(column => column.attr !== "updatedAt");
          if (selectedExpiration === "recently_expired") new_table.columns.push({
            attr: "updatedAt", name: "Atualização", style: { textAlign: "center" },
            styleContent: { fontSize: 14, textAlign: "center" },
          });
          new_table.data = [];
          new_contracts.forEach((contract) => {
            const history = (typeof contract.history === "string"
              ? JSON.parse(contract.history) : contract.history || []) as historyAttributes[];
            const last_history = history[history.length - 1];
            new_table.data.push({
              id: contract.id,
              friendly_id: `#${contract.friendly_id}`,
              name: contract?.name,
              bond: getBond({ contract, theme }),
              signatories: getSignatories({ contract, theme }),
              status: getStatus({ ...contract_status[contract.status] }),
              author: getAuthor({ person: contract, theme }),
              franchise: getFranchise({ person: contract, theme }),
              createdAt: correctDate(contract.createdAt),
              expiration: correctDate(contract.expiries_in),
              updatedAt: correctDate(contract.updatedAt),
              canceledAt:
                contract.status === "canceled"
                  ? correctDate(last_history?.createdAt)
                  : "-",
              signedAt:
                contract.status === "signed"
                  ? correctDate(last_history?.createdAt)
                  : "-",
              this: contract,
            });
          });
          return { ...new_table };
        });

        setNumOfContracts(result.data.count);
        setNumberOfPages(Math.ceil(result.data.count / paginationLimit));
        setUpdatedAt(new Date());
      } catch (err) {
        if (currentRequest !== requestId.current) return;
        notify("Erro na busca pelos documentos eletrônicos!", "error");
      }
      setLoadingContracts(false);
    },
    [franchise, filters, searchString, expiration, paginationLimit, onChangeExpiration]
  );

  const onCancelModal = useCallback(() => {
    setContractSelected(null);
    setOpenModalContract(false);
    searchContracts(atualPage);
  }, [franchise, filters, searchString, atualPage]);

  const onSaveContract = useCallback(() => {
    setOpenModalContract(false);
    searchContracts(1);
  }, [franchise, filters, searchString]);

  const handleOpenModalContract = useCallback(async (contract) => {
    setContractSelected({ ...contract });
    setOpenModalContract(true);
  }, []);

  const confirmCancelContract = useCallback(async () => {
    setOpenConfirmCancel(false);
    setLoadingCancel(true);
    try {
      await api.put(`/contracts/cancel/${contractSelected?.id}`);
      notify("Documento eletrônico cancelado com sucesso!", "success");
      setContractSelected(null);
      searchContracts(atualPage);
    } catch (err) {
      notify("Erro ao cancelar documento eletrônico!", "error");
    }
    setLoadingCancel(false);
  }, [contractSelected, atualPage, franchise, filters, searchString]);

  const onClickCancelContract = useCallback(async (contract) => {
    setContractSelected({ ...contract });
    setOpenConfirmCancel(true);
  }, []);

  const closeConfirmCancel = useCallback(() => {
    setOpenConfirmCancel(false);
    setContractSelected(null);
  }, []);

  const handleAction = (action_id: string) => {
    if (action_id === "new_item") setOpenModalContract(true);
    else if (action_id === "go_to_models")
      navigate("/settings/real_estate/models-documents-electronic");
    else console.log({ action_id });
  };

  const getQueryContractId = useCallback(async () => {
    const query = window.location.search.split("?").pop();
    const queries = query ? query.split("&") : [""];
    let contractId: any = queries.find(
      (item: string) => item.split("=")[0] === "contractId"
    );
    contractId = contractId ? contractId.split("=")[1] : null;
    if (contractId) {
      const { data } = await api.get(`/contracts/${contractId}`);
      setContractSelected({ ...data });
      setOpenModalContract(true);
    }
  }, []);

  useEffect(() => {
    getQueryContractId();
  }, []);

  useEffect(() => {
    searchContracts(1);
    return () => { requestId.current++; };
  }, [franchise?.id, paginationLimit]);

  return (
    <PageList
      updatedAt={updatedAt}
      loading={loadingContracts}
      setLoading={setLoadingContracts}
      numberOfItems={numOfContracts}
      textHeader="Documentos Eletrônicos"
      textItems="documentos eletrônicos encontrados"
      text_loading={"Buscando Documentos Eletrônicos"}
      text_empty={"Nenhum documento eletrônico encontrado"}
      theme={theme}
      initialFilters={listFilters}
      onChangeFilters={setFilters}
      table={table}
      have_edit
      have_cancel={user.type !== "root"}
      have_download
      getUrlDownload={(contract) =>
        contract.file_signed_url
          ? contract.file_signed_url
          : contract.file_original_url
      }
      canDownload={(contract) =>
        ["signed", "pending"].includes(contract.status) &&
        (contract.file_signed_url || contract.file_original_url)
      }
      canCancel={(contract) => ["pending"].includes(contract.status)}
      edit_action={(contract) => handleOpenModalContract(contract)}
      cancel_action={(contract) => onClickCancelContract(contract)}
      search={searchContracts}
      pages={numberOfPages}
      page={atualPage}
      onClickOption={handleAction}
      actions={actionsInitial}
      onChangeSearch={setSearchString}
      getTextDownload={(contract) =>
        contract.file_signed_url
          ? "Documento eletrônico Assinado"
          : "Documento eletrônico Original"
      }
    >
      {openModalContract && (
        <ModalContract
          onCancel={onCancelModal}
          onSave={onSaveContract}
          openModal={openModalContract}
          backgroundVisible={false}
          defaultContract={copyOf(contractSelected)}
        />
      )}

      <ModalAttention
        theme={theme}
        open={openConfirmCancel}
        cancelAction={closeConfirmCancel}
        confirmAction={confirmCancelContract}
        cancelText="Voltar"
        content={
          <>
            Você deseja cancelar o documento eletrônico{" "}
            <b>{contractSelected?.name?.toUpperCase()}</b> ?
          </>
        }
      />

      <ModalLoading loading={loadingCancel} theme={theme} />
    </PageList>
  );
};
